import {
  Directive,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChange,
} from '@angular/core';
import { anyChanged, isPresent } from '@progress/kendo-angular-common';
import {
  ColumnBase,
  ColumnComponent,
  ColumnReorderEvent,
  DataBindingDirective,
  DataStateChangeEvent,
  GridComponent,
} from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { Subscription, first } from 'rxjs';
import {
  GridStateConfig,
  KendoGridState,
  LocalStorageKeyPrefix,
} from './kendo-grid-state.models';

/**
 * A directive that automatically saves and restores Kendo Grid state to localStorage.
 * 
 * This directive handles the persistence of grid configuration including column order,
 * visibility, sizing, sorting, filtering, and grouping state. The state is automatically
 * saved when the user makes changes and restored when the grid is initialized.
 *
 * @example
 * ```html
 * <kendo-grid [gridState]="'myGridId'" ...></kendo-grid>
 * ```
 * 
 * @example
 * ```html
 * <kendo-grid [gridState]="{ gridId: 'myGrid', version: '2.0' }" ...></kendo-grid>
 * ```
 */
@Directive({
  selector: '[gridState]',
  standalone: true,
})
export class KendoGridStateDirective implements OnInit, OnDestroy, OnChanges {
  /**
   * Default configuration settings for grid state management.
   * Provides sensible defaults for new grid instances.
   */
  public static defaultConfig: GridStateConfig = {
    gridId: '',
    version: '1',
    loadState: true,
    absoluteColumnSizes: false,
  };

  /** @internal */
  private _config: GridStateConfig = {
    ...KendoGridStateDirective.defaultConfig,
  };

  /**
   * Gets the current grid state configuration.
   */
  public get config(): GridStateConfig {
    return this._config;
  }

  /**
   * Sets the grid state configuration.
   */
  public set config(value: GridStateConfig) {
    this._config = value;
  }

  /**
   * The grid state configuration or grid ID.
   * Can be a string (grid ID) or a full configuration object.
   * @default undefined
   */
  @Input('gridState') public set initConfig(
    value: string | GridStateConfig
  ) {
    if (typeof value === 'string') {
      this._config.gridId = value ?? '';
    } else {
      this._config = value;
      this._version = value.version ?? '1';
    }
  }

  /**
   * Gets the localStorage key used to store grid state.
   * Combines the prefix with the grid ID.
   */
  public get localStorageKey(): string {
    if (!this.config.gridId) {
      throw new Error(
        'Grid ID must be provided to use KendoGridStateDirective'
      );
    }

    return `${LocalStorageKeyPrefix}${this.config.gridId}`;
  }

  /** @internal */
  private _version: string = '1';
  
  /**
   * Gets the version string used for state compatibility checks.
   * Ensures backwards compatibility when grid structure changes.
   */
  get version(): string {
    if (this._version !== '1' && this.config.version !== '1' && this._version !== this.config.version) {
        throw new Error('Cannot set version both in gridState config and through version Input. Please use only one method.');
    }
    return this._version;
  }
  
  /**
   * The version string for state compatibility.
   * Used to invalidate old state when grid structure changes.
   * @default '1'
   */
  @Input() public set version(value: string) {
    this._version = value;
  }

  /** @internal */
  private _loadState: boolean = true;
  
  /**
   * Gets whether state should be automatically loaded on initialization.
   */
  get loadState(): boolean {
    return this._loadState;
  }
  
  /**
   * Whether to automatically load saved state when the grid initializes.
   * Set to false to prevent automatic state restoration.
   * @default true
   */
  @Input() public set loadState(value: boolean) {
    this._loadState = isPresent(value) ? value : true;
  }

  /** @internal */
  private _subscriptions: Subscription[] = [];
  
  /** @internal */
  private _fieldIndexes: Map<string, number> = new Map<string, number>();
  
  /** @internal */
  private _columnIndexes: Map<ColumnComponent, number> = new Map<
    ColumnComponent,
    number
  >();

  /** @internal */
  protected state: State = {
    skip: 0,
  };

  /**
   * Creates a new instance of the KendoGridStateDirective.
   * @param grid The grid component instance to manage state for
   */
  constructor(private grid: GridComponent) { }

  /**
   * Generates a unique key for identifying grid columns across sessions.
   * Combines field name, index, and title to handle dynamic column scenarios.
   * @param column The column component to generate a key for
   * @returns A unique string identifier for the column
   * @internal
   */
  protected getColumnKey(column: ColumnComponent): string {
    let field = column.field ?? '';
    let title = column.title ?? '';

    let fieldIndex = this._columnIndexes.get(column);
    if (fieldIndex === undefined) {
      fieldIndex = (this._fieldIndexes.get(field) ?? -1) + 1;
      this._fieldIndexes.set(field, fieldIndex);
      this._columnIndexes.set(column, fieldIndex);
    }

    let columnKey = `${field}-${fieldIndex}-${title}`;
    return columnKey;
  }

  /**
   * Initializes the directive and sets up event subscriptions.
   * Loads saved state if available and configures automatic saving.
   */
  public ngOnInit(): void {
    if (this.loadState) {
      let state = this.getGridState();
      if (state) {
        this.grid.columnsContainer.changes.pipe(first()).subscribe(() => {
          setTimeout(() => {
            this.applyGridState(state);
          });
        });
      } else {
        setTimeout(() => {
          this.grid.filterChange.emit(this.grid.filter!);
        });
      }
    }

    this._subscriptions.push(
      this.grid.dataStateChange.subscribe((e) => {
        if (!this._applyingGridState) {
          this.onStateChange(e);
        }
      })
    );

    this._subscriptions.push(
      this.grid.columnReorder.subscribe((e) => {
        if (!this._applyingGridState) {
          this.columnReorder(e);
        }
      })
    );

    this._subscriptions.push(
      this.grid.columnResize.subscribe((e) => {
        if (!this._applyingGridState) {
          this.saveState(this.state);
        }
      })
    );

    this._subscriptions.push(
      this.grid.columnVisibilityChange.subscribe((e) => {
        if (!this._applyingGridState) {
          this.saveState(this.state);
        }
      })
    );
  }

  /**
   * Cleans up subscriptions when the directive is destroyed.
   */
  public ngOnDestroy(): void {
    for (let subscription of this._subscriptions) {
      subscription.unsubscribe();
    }
  }

  /**
   * Handles input property changes and saves state when relevant properties change.
   * @param changes Object containing the changed properties
   * @internal
   */
  public ngOnChanges(changes: { [propertyName: string]: SimpleChange }): void {
    if (anyChanged(['pageSize', 'skip', 'sort', 'group', 'filter'], changes)) {
      this.saveState(this.state);
    }
  }

  /**
   * Handles grid data state changes and triggers state saving.
   * @param state The new data state from the grid
   * @internal
   */
  public onStateChange(state: DataStateChangeEvent): void {
    this.saveState(state);
  }

  /**
   * Retrieves the saved grid state from localStorage.
   * Validates version compatibility before returning the state.
   * @returns The saved grid state or null if none exists or version mismatch
   */
  public getGridState(): KendoGridState | null {
    let data = localStorage.getItem(this.localStorageKey);
    if (data) {
      let state = JSON.parse(data) as KendoGridState;
      if (
        state.DataType === 'KendoGridState' &&
        state.Version === this.version
      ) {
        return state;
      }
    }

    return null;
  }

  /** @internal */
  private _applyingGridState: boolean = false;

  /**
   * Applies a saved grid state to the current grid instance.
   * Restores column configuration, sorting, filtering, and grouping.
   * @param state The grid state to apply
   */
  public applyGridState(state: KendoGridState): void {
    this._applyingGridState = true;
    try {
      if (state.tableWidth && this.config.absoluteColumnSizes) {
        this.grid.wrapper.nativeElement.querySelector('.k-grid-table').style[
          'width'
        ] = state.tableWidth;
        this.grid.header.nativeElement.querySelector('table').style['width'] =
          state.tableWidth;
      }

      let allColumns = this.grid.columnList.toArray();
      let columnKeys = new Map<string, ColumnComponent>();
      allColumns.forEach((column, currentIndex) => {
        let c = column as ColumnComponent;
        let key = this.getColumnKey(c);
        columnKeys.set(key, c);
      });

      let reorderColumns = state.columns.filter((column) => column.orderIndex > 0).length > 0;

      if (reorderColumns) {
        state.columns.sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
      }

      state.columns.forEach((columnState) => {
        let column = columnKeys.get(columnState.key!);

        if (column) {
          column.hidden = columnState.hidden ?? false;
          if (columnState.width) {
            column.width = columnState.width;
          }
          if (reorderColumns && columnState.orderIndex !== undefined) {
            this.grid.reorderColumn(column, columnState.orderIndex);
          }
        }
      });

      this.grid.sort = state.dataSourceOptions.sort!;
      this.grid.group = state.dataSourceOptions.group!;
      this.grid.filter = state.dataSourceOptions.filter!;

      this.grid.dataStateChange.emit({
        filter: this.grid.filter!,
        group: this.grid.group!,
        skip: this.grid.skip!,
        sort: this.grid.sort!,
        take: this.grid.pageSize!,
      });

      this.grid.filterChange.emit(this.grid.filter!);
    } finally {
      this._applyingGridState = false;
    }
  }

  /**
   * Builds a complete grid state object for saving.
   * Captures current column configuration and data source options.
   * @param state The current grid state containing sort, group, and filter
   * @returns Complete grid state object ready for serialization
   * @internal
   */
  protected buildSaveState({ sort, group, filter }: State): KendoGridState {
    let data: KendoGridState = {
      DataType: 'KendoGridState',
      Version: this.version,
      tableWidth:
        this.grid.wrapper.nativeElement.querySelector('.k-grid-table').style[
        'width'
        ],
      dataSourceOptions: {
        sort,
        group,
        filter,
      },
      columns: this.grid.columnList.toArray().map((column, index) => {
        let c = column as ColumnComponent;
        let key = this.getColumnKey(c);

        return {
          key: key,
          field: c.field,
          hidden: c.hidden,
          width: c.width,
          title: c.title,
          orderIndex: c.orderIndex,
        };
      }),
    };

    data.columns.sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

    return data;
  }

  /**
   * Saves the current grid state to localStorage.
   * @param state The current grid state containing sort, group, and filter
   * @internal
   */
  protected saveState({ sort, group, filter }: State): void {
    let data = this.buildSaveState({ sort, group, filter });
    this._save(data);
  }

  /**
   * Handles column reorder events and updates the saved state.
   * @param e The column reorder event containing old and new positions
   * @internal
   */
  protected columnReorder(e: ColumnReorderEvent): void {
    let data = this.buildSaveState(this.state);
    let col = data.columns.splice(e.oldIndex, 1)[0];
    data.columns.splice(e.newIndex, 0, col);

    for (let i = 0; i < data.columns.length; i++) {
      data.columns[i].orderIndex = i;
    }

    this._save(data);
  }

  /**
   * Saves grid state data to localStorage.
   * @param data The grid state data to save
   * @internal
   */
  private _save(data: KendoGridState) {
    localStorage.setItem(this.localStorageKey, JSON.stringify(data));
  }

  /**
   * Clears the saved state for this grid instance.
   * Removes the state from localStorage completely.
   */
  public clearState(): void {
    localStorage.removeItem(this.localStorageKey);
  }

  /**
   * Clears all saved grid states matching the specified grid ID pattern.
   * Useful for cleanup operations or when grid structure changes significantly.
   * @param gridId Optional grid ID prefix to match. Empty string clears all grid states.
   */
  public static clearAllStates(gridId: string = ''): void {
    let prefix = `${LocalStorageKeyPrefix}${gridId}`;
    for (let key in localStorage) {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    }
  }
}